import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./lib/auth/AuthContext";
import { ProtectedRoute } from "./lib/auth/ProtectedRoute";
import { ThemeProvider } from "./lib/theme/ThemeProvider";
import { PageViewTracker } from "./components/PageViewTracker";
import { Sentry } from "./integrations/sentry";
import { jitterDelayMs } from "@omnara/shared-js";
import { TooltipManagerProvider } from "@/hooks/useOnboardingTooltipManager";
import { ConfirmDialogProvider } from "@/components/ConfirmDialogProvider";

// Lazy load all pages for better code splitting
const Landing = lazy(() => import("./pages/Landing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CLIAuth = lazy(() => import("./pages/CLIAuth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Billing = lazy(() => import("./pages/Billing"));
const GitHubCallback = lazy(() => import("./pages/GitHubCallback"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourcePage = lazy(() => import("./pages/ResourcePage"));
const ManagedAgents = lazy(() => import("./pages/ManagedAgents"));
const ApiProvider = lazy(() => import("./lib/api/ApiProvider"));

const MAX_QUERY_RETRY_DELAY_MS = 30_000;
const MIN_QUERY_RETRY_DELAY_MS = 250;
const QUERY_RETRY_JITTER_RATIO = 0.2;

const queryRetryDelay = (attemptIndex: number) => {
  const baseDelay = Math.min(1000 * Math.pow(2, attemptIndex), MAX_QUERY_RETRY_DELAY_MS);
  return jitterDelayMs(baseDelay, {
    jitterRatio: QUERY_RETRY_JITTER_RATIO,
    minDelayMs: MIN_QUERY_RETRY_DELAY_MS,
    maxDelayMs: MAX_QUERY_RETRY_DELAY_MS,
  });
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      retryDelay: queryRetryDelay,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Fallback UI shown when an unrecoverable error occurs
function ErrorFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#1A1A1A] text-white p-8">
      <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
      <p className="text-white/70 mb-6 text-center max-w-md">
        We've been notified and are working to fix it. Please try refreshing the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
      >
        Refresh Page
      </button>
    </div>
  );
}

// Minimal loading fallback for lazy-loaded pages (keeps initial bundle small)
function PageLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
    </div>
  );
}

const App = () => (
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system">
        <AuthProvider>
          <TooltipManagerProvider>
            <TooltipProvider>
              <ConfirmDialogProvider />
              <BrowserRouter>
                <PageViewTracker />
                <Suspense fallback={<PageLoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/try" element={<Landing />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/resources" element={<Resources />} />
                    <Route path="/resources/:slug" element={<ResourcePage />} />
                    <Route
                      path="/cli-auth"
                      element={
                        <ApiProvider requireCollections={false}>
                          <CLIAuth />
                        </ApiProvider>
                      }
                    />
                    <Route
                      path="/github/callback"
                      element={
                        <ApiProvider requireCollections={false}>
                          <GitHubCallback />
                        </ApiProvider>
                      }
                    />

                    <Route
                      path="/dashboard/*"
                      element={
                        <ProtectedRoute>
                          <ApiProvider>
                            <Dashboard />
                          </ApiProvider>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/billing"
                      element={
                        <ProtectedRoute>
                          <ApiProvider>
                            <Billing />
                          </ApiProvider>
                        </ProtectedRoute>
                      }
                    />

                    <Route path="/managed" element={<ManagedAgents />} />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </TooltipManagerProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
